class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    // Search Bar PRD 3.7: strip special characters/emoji and run the search
    // on what remains. PRD 3.2/1.7: no API call below 3 searchable characters
    // — the results panel closes so the blank state stays visible instead.
    const searchableTerm = this.sanitizeQuery(this.searchTerm);
    if (searchableTerm.length < 3) {
      this.close();
      return;
    }

    this.getSearchResults(searchableTerm);
  }

  sanitizeQuery(term) {
    // Keep letters (any script), numbers, spaces, hyphens and apostrophes;
    // drop emoji and other symbols, then collapse leftover whitespace.
    return term
      .replace(/[^\p{L}\p{N}\s'-]/gu, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  onFormSubmit(event) {
    const selected = this.querySelector('[aria-selected="true"] a');
    // Price query ("under 500" / "over 1000") committed with Enter → go to the
    // SRP with Shopify's native price filter instead of a text search. Skipped
    // when the shopper has arrow-selected a specific result card (that card's
    // own link should win). q=* matches all products so a pure price query
    // ("under 500", no product name) still returns the full catalog to filter.
    const price = this.parsePriceQuery(this.getQuery());
    if (price && !selected) {
      event.preventDefault();
      window.location.href = this.buildPriceUrl(price);
      return;
    }
    // Text query committed with Enter: redirect with a trailing "*" so the
    // committed SRP prefix-matches half-typed terms the way the overlay's
    // predictive search does ("smoo" -> "Smooth Operator"). main-search.liquid +
    // srp-infinite-scroll.js then name-filter the results back to genuine
    // matches (keeping the synonym/boost fallback). Skipped when a result card
    // is arrow-selected (its own link wins).
    const term = this.sanitizeQuery(this.getQuery());
    if (term.length && !selected) {
      event.preventDefault();
      const action = this.input.form.getAttribute('action') || '/search';
      window.location.href = `${action}?q=${encodeURIComponent(term)}*&type=product&options%5Bprefix%5D=last`;
      return;
    }
    if (!this.getQuery().length || selected) event.preventDefault();
  }

  /**
   * Parse a price intent out of the raw query. Supports only the two forms the
   * client asked for: "under/below/less than/upto X" (price <= X) and
   * "over/above/more than X" (price >= X). Returns { text, gte, lte } where
   * `text` is whatever product term is left after removing the price phrase
   * (e.g. "lip under 500" -> text "lip"), or null when there's no price intent.
   */
  parsePriceQuery(raw) {
    if (!raw) return null;
    // Drop currency symbols and thousands separators so "₹1,000" parses.
    const norm = String(raw).replace(/[₹$,]/g, ' ').replace(/\s+/g, ' ').trim();
    // Capture the actual preposition (group 1) so the SRP label can read back
    // "below ₹200" for "below 200" and "under ₹200" for "under 200", instead of
    // always normalising to "under"/"over".
    const under = norm.match(/\b(under|below|less\s+than|upto|up\s+to|less)\s+(\d+(?:\.\d+)?)\b/i);
    const over = norm.match(/\b(over|above|more\s+than|greater\s+than|more)\s+(\d+(?:\.\d+)?)\b/i);
    const hit = under || over;
    if (!hit) return null;
    const amount = Math.round(parseFloat(hit[2]));
    if (!isFinite(amount) || amount <= 0) return null;
    const prep = hit[1].replace(/\s+/g, ' ').toLowerCase();
    const text = norm
      .replace(hit[0], ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return under ? { text, gte: null, lte: amount, prep } : { text, gte: amount, lte: null, prep };
  }

  buildPriceUrl(price) {
    const params = new URLSearchParams();
    // Append "*" to the text part so a partial product term ("smoo under 500")
    // prefix-matches on the committed SRP too; srp-infinite-scroll.js name- and
    // price-filters the results. Pure price queries stay q=* (match all).
    params.set('q', price.text ? price.text + '*' : '*');
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    // Discounted-price bounds ride as CUSTOM params (sb_d*): Shopify ignores
    // them — so it returns the full matching set, NOT pre-narrowed by the
    // regular variant.price — and srp-infinite-scroll.js filters the loaded
    // cards by the SHOWN (discounted) price. gte defaults to ₹1 to drop free
    // items. NO sort_by (default order, like the overlay's nav-price-results):
    // ordering by Shopify's REGULAR price would bury a deeply-discounted
    // product (low SHOWN price, high regular price) far down the list, past the
    // pages srp-infinite-scroll.js auto-loads — so it'd never be filtered in on
    // the SRP even though the overlay (which pulls the default-order batch)
    // shows it. Default order keeps the SRP's candidate set a superset of the
    // overlay's, so the two agree.
    params.set('sb_dgte', price.gte != null ? price.gte : 1);
    if (price.lte != null) params.set('sb_dlte', price.lte);
    // Carry the typed preposition so the SRP label reads it back verbatim.
    if (price.prep) params.set('sb_prep', price.prep);
    const base = (typeof routes !== 'undefined' && routes.search_url) || '/search';
    return `${base}?${params.toString()}`;
  }

  /** True and shown-price range for filtering cards by their .pvc__price-now. */
  matchesShownPrice(el, price) {
    const now = el.querySelector('.pvc__price-now');
    if (!now) return false;
    // Keep the decimal: "₹852.50" -> 852.5, not 85250. Commas are already
    // stripped from the rendered price, so [^\d.] is safe here.
    const value = parseFloat(now.textContent.replace(/[^\d.]/g, ''));
    if (!isFinite(value)) return false;
    const floor = price.gte != null ? price.gte : 1; // ₹1 default drops free items
    if (value < floor) return false;
    if (price.lte != null && value > price.lte) return false;
    return true;
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;
    // Same 3-searchable-character gate as onChange — refocusing a field
    // holding 1-2 characters must not fire a request either.
    if (this.sanitizeQuery(currentSearchTerm).length < 3) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.sanitizeQuery(this.searchTerm));
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;
    // split() instead of Dawn's RegExp: the previous term is raw user input,
    // so regex chars could throw, and a no-match .match() returns null whose
    // .length crashed onChange — killing live search until a page refresh
    // (the stuck-skeleton bug).
    const occurrences = currentButtonText.split(previousTerm).length - 1;
    // 0 = stale text from an older query; >1 = ambiguous replacement.
    if (occurrences !== 1) return;
    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    // Price query ("under 500" / "over 1000") — the predictive/suggest endpoint
    // can't filter by price, so fetch the /search endpoint (which applies
    // Shopify's native filter.v.price) rendered through the nav-price-results
    // section, which emits the same compact cards the suggest section does.
    const price = this.parsePriceQuery(this.searchTerm);
    if (price) {
      this.getPriceResults(price);
      return;
    }

    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // A real network request is starting (cache hits above render instantly
    // and never get here) — let <nav-search> show the loading skeleton panel
    // (Figma 1237:7072) while it is in flight.
    this.dispatchEvent(new CustomEvent('predictive-search:loading', { bubbles: true }));

    // Overlay results come from the COMMITTED /search endpoint (rendered through
    // the nav-search-results section), NOT /search/suggest. Same engine, same
    // `q=<term>*&type=product` query and same relevance order as the SRP that
    // Enter / See All lands on, so the overlay is the first few rows of that SRP
    // instead of a second, disagreeing result set. See sections/
    // nav-search-results.liquid for the full rationale; price queries take the
    // parallel getPriceResults path above.
    const searchBase = (typeof routes !== 'undefined' && routes.search_url) || '/search';
    const url = `${searchBase}?q=${encodeURIComponent(searchTerm)}*&type=product&section_id=nav-search-results`;

    fetch(url, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-nav-search-results');
        const resultsMarkup = section ? section.innerHTML : '';
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        // Let the surrounding <nav-search> show the designed error state
        // (Figma 1237:2895) instead of leaving a silently-empty dropdown.
        this.dispatchEvent(new CustomEvent('predictive-search:error', { bubbles: true }));
        throw error;
      });
  }

  /**
   * Fetch price-filtered products for the dropdown (mirror of getSearchResults
   * for text queries). Uses the /search endpoint + nav-price-results section so
   * filter.v.price applies, then renders the returned compact cards through the
   * same results pipeline (predictive-search:results -> nav-search lifts them).
   */
  getPriceResults(price) {
    // Cache by TEXT only (not the amount): the candidate set is identical for
    // every "under/over X" of the same term — JS re-filters by X each time.
    const cacheKey = `price:${(price.text || '*').toLowerCase()}`;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[cacheKey]) {
      this.renderPriceResults(this.cachedResults[cacheKey], price);
      return;
    }

    this.dispatchEvent(new CustomEvent('predictive-search:loading', { bubbles: true }));

    const params = new URLSearchParams();
    params.set('q', price.text || '*');
    params.set('type', 'product');
    params.set('section_id', 'nav-price-results');
    params.set('options[prefix]', 'last');
    const base = (typeof routes !== 'undefined' && routes.search_url) || '/search';

    fetch(`${base}?${params.toString()}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-nav-price-results');
        const markup = section ? section.innerHTML : '';
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[cacheKey] = markup;
        });
        this.renderPriceResults(markup, price);
      })
      .catch((error) => {
        if (error?.code === 20) return; // aborted
        this.close();
        this.dispatchEvent(new CustomEvent('predictive-search:error', { bubbles: true }));
        throw error;
      });
  }

  renderPriceResults(resultsMarkup, price) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // Keep only cards whose SHOWN (discounted) price is in range, capped to a
    // handful for the dropdown; drop the rest so nav-search-toggle's lift
    // (which grabs every .nav-search-product) only ever sees matches.
    const list = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
    if (list) {
      const MAX = 8;
      let kept = 0;
      [...list.querySelectorAll('.predictive-search__list-item')].forEach((li) => {
        const card = li.querySelector('.nav-search-product');
        if (kept < MAX && card && this.matchesShownPrice(card, price)) {
          kept += 1;
        } else {
          li.remove();
        }
      });
    }

    const hasProducts = this.predictiveSearchResults.querySelector(
      '#predictive-search-results-products-list .nav-search-product'
    );
    if (!hasProducts) {
      this.close();
      this.dispatchEvent(
        new CustomEvent('predictive-search:zero', { bubbles: true, detail: { term: this.searchTerm } })
      );
      return;
    }

    this.removeAttribute('loading');
    this.open();
    this.dispatchEvent(new CustomEvent('predictive-search:results', { bubbles: true }));
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // Zero products returned: keep Dawn's dropdown closed and let the
    // surrounding <nav-search> show the designed zero-results state
    // (Figma 1237:2368 — message + trending products) instead.
    const hasProducts = this.predictiveSearchResults.querySelector(
      '#predictive-search-results-products-list .predictive-search__list-item'
    );
    if (!hasProducts) {
      this.close();
      this.dispatchEvent(
        new CustomEvent('predictive-search:zero', { bubbles: true, detail: { term: this.searchTerm } })
      );
      return;
    }

    this.setLiveRegionResults();
    this.open();
    // Results are on screen — <nav-search> hides the loading skeleton panel.
    this.dispatchEvent(new CustomEvent('predictive-search:results', { bubbles: true }));
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    // The committed-search section (nav-search-results) renders cards only — no
    // live-region count element, unlike Dawn's suggest section. Announce the card
    // count instead of dereferencing an element that isn't there.
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
      return;
    }
    const count = this.predictiveSearchResults.querySelectorAll(
      '#predictive-search-results-products-list .predictive-search__list-item'
    ).length;
    this.setLiveRegionText(`${count} ${count === 1 ? 'result' : 'results'}`);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
